import { useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme, clamp, changeColorOpacity } from '@shopify/polaris-viz-core';
import { TOOLTIP_BG_OPACITY } from '../../../../constants.js';
import { useBrowserCheck } from '../../../../hooks/useBrowserCheck.js';
import styles from './AnnotationContent.scss.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const MAX_WIDTH = 350;
function AnnotationContent({
  annotation,
  drawableWidth,
  index,
  onMouseLeave,
  parentRef,
  tabIndex,
  x,
  y
}) {
  var _bounds$width;

  const selectedTheme = useTheme();
  const {
    isFirefox
  } = useBrowserCheck();
  const [ref, setRef] = useState(null);
  const [bounds, setBounds] = useState();
  useLayoutEffect(() => {
    setBounds(ref === null || ref === void 0 ? void 0 : ref.getBoundingClientRect());
  }, [ref]);
  useEffect(() => {
    const tooltip = document.querySelector('[data-tooltip]');

    if (tooltip) {
      tooltip.style.display = 'none';
    }

    return () => {
      if (tooltip) {
        tooltip.style.display = 'block';
      }
    };
  }, []);

  if (annotation.content == null) {
    return null;
  }

  const {
    content,
    title,
    linkText = 'Learn more',
    linkUrl
  } = annotation.content;
  const width = (_bounds$width = bounds === null || bounds === void 0 ? void 0 : bounds.width) !== null && _bounds$width !== void 0 ? _bounds$width : 0;
  let xPosition = x - width / 2;

  if (xPosition + width > drawableWidth) {
    xPosition = drawableWidth - width;
  }

  return /*#__PURE__*/createPortal( /*#__PURE__*/jsx(Wrapper, {
    height: "100%",
    width: "100%",
    style: {
      pointerEvents: 'none',
      overflow: 'visible'
    },
    x: clamp({
      amount: xPosition,
      min: 0,
      max: drawableWidth
    }),
    y: y,
    parentRef: parentRef,
    children: /*#__PURE__*/jsxs("div", {
      className: styles.Container,
      "data-block-tooltip-events": true,
      onMouseLeave: onMouseLeave,
      ref: setRef,
      style: {
        width: 'fit-content',
        maxWidth: Math.min(drawableWidth, MAX_WIDTH),
        // Firefox doesn't support blur so we'll remove
        // the opacity on this element.
        background: changeColorOpacity(selectedTheme.tooltip.backgroundColor, isFirefox ? 1 : TOOLTIP_BG_OPACITY)
      },
      id: `annotation-content-${index}`,
      role: "dialog",
      children: [title != null && /*#__PURE__*/jsx("p", {
        className: styles.Title,
        style: {
          color: selectedTheme.tooltip.textColor
        },
        role: "heading",
        "aria-level": 2,
        children: title
      }), /*#__PURE__*/jsxs("p", {
        className: styles.Content,
        style: {
          color: selectedTheme.tooltip.textColor
        },
        "data-is-annotation-content": true,
        children: [content, linkUrl != null && /*#__PURE__*/jsx("a", {
          href: linkUrl,
          className: styles.Link,
          tabIndex: tabIndex,
          style: {
            color: selectedTheme.annotations.linkColor
          },
          children: linkText
        })]
      })]
    })
  }), parentRef !== null && parentRef !== void 0 ? parentRef : document.body);
}

function Wrapper({
  children,
  parentRef,
  ...theRest
}) {
  const Tag = parentRef ? 'foreignObject' : 'div';
  return /*#__PURE__*/jsx(Tag, { ...theRest,
    children: children
  });
}

export { AnnotationContent };
